import React, { useEffect, useState, useRef, Fragment } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import { DeleteActionIcon, SelectAllIcon, NotificationsIcon } from '../Icons';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import useNotifications, { PAGE_SIZE } from '../hooks/useNotifications';
import { useAuth } from '../AuthProvider';
import text from '../lib/dictionary.json';
import '../pages/modal.css';
import './notification.css';
import axios from 'axios';
import { AXIOS_OPTIONS, BASE_URL_API } from '../Constants';
import {
    Checkbox,
    FormControlLabel,
    Box,
    Typography
} from '@mui/material';
import { useNavigate } from 'react-router';
import NotificationPopup from './NotificationPopup';
import { get, post } from '../services/http';

const MyCheckbox = ({ checked, placement, yesLabel, noLabel, onCheck }) => (
    <FormControlLabel
        onClick={(e) => e.stopPropagation()}
        labelPlacement={placement}
        control={
            <Checkbox
                checked={checked}
                onChange={onCheck}
                sx={{
                    color: 'var(--accent)',
                    '&.Mui-checked': {
                        color: 'var(--accent)',
                    },
                }} />
        } label={!yesLabel && !noLabel
            ? null
            : <Box sx={{
                width: '100%',
                display: 'flex',
                alignItems: 'center'
            }}>
                <Typography
                    variant="body1"
                    sx={{ color: 'var(--header-color)' }}>
                    {checked ? noLabel : yesLabel}
                </Typography>
            </Box>
        } sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '2px',
            margin: 0,
            '& .MuiFormControlLabel-label': {
                fontSize: '14px',
                textAlign: placement
            }
        }} />
);

const getLinkFromTrigger = (id, trigger, userType) => {
    const links = {
        task: '/transportation/transportation_tasks/details',
        meeting: '/meetings/hall_booking/details',
        ticket: '/bookings/ticket_booking/details',
        hotelbooking: '/bookings/hotel_booking/details',
        MaintenanceRequest: '/maintenance_request/details',
        homeassessmentrequest: '/customer_happiness/home_assessment/details',
        customerfeedback: '/customer_happiness/feedback'
    };

    return `${links[trigger]}/${id}`;
};


const NotificationItem = ({ notification, toggleSelect, selected, setLoading, closeSidePanel }) => {
    const { lang, setAlertState, setConfirmState, user } = useAuth();
    const navigate = useNavigate();

    const deleteNotification = () => {
        setConfirmState({
            open: true,
            type: 'delete',
            message: text[lang]['delete-confirm'],
            onConfirm: async () => {
                setLoading(notification.id, 'delete', true);

                try {
                    const { data: deletedNotification } = await get('/notifications/delete/' + notification.id);
                    setAlertState({
                        open: true,
                        message: `(${deletedNotification.id} - ${deletedNotification.title}) deleted successfully`,
                        type: 'success'
                    });
                } catch (error) {
                    setAlertState({ open: true, message: error.response?.data?.message ?? error.message, type: 'error' });
                }
            }
        });
    };

    const markNotification = async (read, alert = true) => {
        setLoading(notification.id, 'mark', true);

        try {
            const { data: updatedNotification } = await post(
                 '/notifications/update/' + notification.id,
                JSON.stringify({ read }),
                AXIOS_OPTIONS
            );
            alert && setAlertState({
                open: true,
                message: `(${updatedNotification.id} - ${updatedNotification.title}) updated successfully`,
                type: 'success'
            });
        } catch (error) {
            setAlertState({ open: true, message: error.response?.data?.message ?? error.message, type: 'error' });
        }
    };

    return (
        <div className='notification-container' onClick={() => {
            markNotification(true, false);
            navigate(getLinkFromTrigger(notification.row_id, notification.trigger, user.type));
            closeSidePanel();
        }}>
            <div className='notification-edge' style={{
                backgroundColor: `var(--${notification.read ? 'primary' : 'error-bg'})`,
                borderRadius: lang === 'en' ? '0 8px 8px 0' : '8px 0 0 8px'
            }}></div>
            {notification.loading.check
                ? <AutorenewIcon style={{ animation: 'rotate .5s linear infinite' }} />
                : <MyCheckbox
                    checked={selected.includes(notification.id)}
                    placement={lang === 'en' ? 'end' : 'start'}
                    yesLabel={''}
                    noLabel={''}
                    onCheck={(e) => toggleSelect(e.target.checked)} />}
            <div className='notification-body'>
                <div className='notification-body-part'>
                    <h4>{notification.title}</h4>
                    <span>{notification.created_at}</span>
                </div>
                <div className='notification-body-part'>
                    <p dir='auto' className='notification-message'>{notification.message}</p>
                </div>
                <div className='notification-body-part'>
                    <span className='notification-mark-btn'
                        style={{ color: 'var(--primary)' }}
                        onClick={(e) => {
                            e.stopPropagation();
                            markNotification(!notification.read);
                        }}>
                        {notification.loading.mark
                            ? <AutorenewIcon style={{ animation: 'rotate .5s linear infinite' }} />
                            : text[lang][`mark-${notification.read ? 'unread' : 'read'}`]}
                    </span>
                    <span className='notification-delete-btn'
                        style={{ color: 'var(--error-bg)' }}
                        onClick={(e) => {
                            e.stopPropagation();
                            deleteNotification();
                        }}>
                        {notification.loading.delete
                            ? <AutorenewIcon style={{ animation: 'rotate .5s linear infinite' }} />
                            : text[lang]['actions-delete']}
                    </span>
                </div>
            </div>
        </div>
    );
};

const Notifications = ({ state, setState }) => {
    const {
        lang,
        setAlertState,
        setConfirmState,
        setReviewState,
        setAuto,
        user: { id },
    } = useAuth();
    const [notificationsQueue, setNotificationsQueue] = useState([]);

    const [selected, setSelected] = useState([]);

    const {
        notifications,
        setNotifications,
        resetPage,
        unread,
        fetchMore,
        hasMore,
        loadingPage,

    } = useNotifications(id, lang, setAlertState, setReviewState, setAuto,
        setNotificationsQueue);

    const handleClose = () => {
        setNotificationsQueue(prev => prev.slice(1));
    };

    const bottomRef = useRef(null);

    useEffect(() => {
        if (!bottomRef.current) return;

        const observer = new IntersectionObserver(
            entries => {
                if (entries[0].isIntersecting && hasMore && !loadingPage) {
                    fetchMore();
                }
            },
            {
                root: document.querySelector('.modal-content'),
                rootMargin: '150px'
            }
        );

        observer.observe(bottomRef.current);
        return () => observer.disconnect();
    }, [fetchMore, hasMore, loadingPage]);

    const close = () => {
        setNotifications(prev => prev.length > PAGE_SIZE ? [] : prev);
        resetPage();
        setSelected([]);
        setState({ ...state, open: false });
    };

    const affectAll = async (action) => {
        setNotifications(prev =>
            prev.map(n =>
                selected.includes(n.id)
                    ? { ...n, loading: { ...n.loading, check: true } }
                    : n
            )
        );

        try {
            await post(
                `/notifications/${action}`,
                { ids: selected },
                AXIOS_OPTIONS
            );
            setSelected([]);
            setAlertState({
                open: true,
                type: 'success',
                message: text[lang][`multi-${action}-success`]
            });
        } catch (error) {
            setAlertState({
                open: true,
                type: 'error',
                message: error.response?.data?.message ?? error.message
            });
        }
    };

    const clear = () => {
        affectAll('delete');
        fetchMore();
    };

    const markAll = () => {
        affectAll('mark-all');
    };

    useEffect(() => {
        setState(prev => ({
            ...prev,
            notifications: unread
        }));
    }, [setState, unread]);

    return (
        <React.Fragment>
            {

                notificationsQueue.length > 0 && <NotificationPopup
                    data={notificationsQueue[0]}
                    remaining={notificationsQueue.length - 1}
                    onClose={handleClose}
                />
            }
            <div id='modal'
                style={{ visibility: state.open ? 'visible' : 'hidden' }}
                onClick={close}
            >
                <div
                    id='side-popup'
                    style={lang === 'en' ? {
                        right: state.open ? 0 : '-60%',
                        borderRadius: '16px 0 0 16px',
                        direction: 'ltr'
                    } : {
                        left: state.open ? 0 : '-60%',
                        borderRadius: '0 16px 16px 0',
                        direction: 'rtl'
                    }}
                    onClick={(e) => e.stopPropagation()}
                >
                    <div className='modal-main-container'>
                        <div className='modal-header' style={{ padding: '16px 32px' }}>
                            <div className='modal-header-item'>
                                <div className='modal-header-icon'><NotificationsIcon /></div>
                                <h2>{text[lang]['notifications']} ({notifications.length})</h2>
                            </div>
                            <span className='notification-close-btn' onClick={close}>
                                <CloseIcon />
                            </span>
                        </div>
                        <div className='separator' style={{ marginBottom: 4 }}></div>

                        <div className='modal-content' style={{
                            height: 'calc(100% - 80px)',
                            width: 'calc(100% - 24px)',
                            padding: 0,
                            overflow: 'hidden'
                        }}>
                            <div className='notifications-select-all'>
                                {notifications.length
                                    ? <MyCheckbox
                                        checked={selected.length === notifications.length}
                                        placement={lang === 'en' ? 'end' : 'start'}
                                        yesLabel={text[lang]['select-all']}
                                        noLabel={text[lang]['deselect-all']}
                                        onCheck={(e) => {
                                            const selectedItems = e.target.checked ? notifications.map(n => n.id) : [];
                                            setSelected(selectedItems);
                                        }} />
                                    : null}
                                <div className='modal-header-item'>
                                    {!!selected.length && (
                                        <Fragment>
                                            <span className='notification-mark-btn'
                                                onClick={() => setConfirmState({
                                                    open: true,
                                                    type: 'info',
                                                    message: text[lang]['multi-mark-confirm'],
                                                    onConfirm: markAll
                                                })}>
                                                <SelectAllIcon style={{ transition: 'fill .3s ease' }} />
                                                {text[lang]['mark-all']}
                                            </span>
                                            <span className='notification-delete-btn'
                                                onClick={() => setConfirmState({
                                                    open: true,
                                                    type: 'delete',
                                                    message: text[lang]['multi-delete-confirm'],
                                                    onConfirm: clear
                                                })}>
                                                <DeleteActionIcon style={{ transition: 'fill .3s ease' }} />
                                                {text[lang]['actions-delete']}
                                            </span>
                                        </Fragment>
                                    )}
                                </div>
                            </div>
                            <div className='notifications-items'>
                                {notifications.map((notification, idx) => (
                                    <NotificationItem
                                        key={idx}
                                        notification={notification}
                                        selected={selected}
                                        closeSidePanel={close}
                                        toggleSelect={checked =>
                                            setSelected(prev =>
                                                checked
                                                    ? [...prev, notification.id]
                                                    : prev.filter(id => id !== notification.id)
                                            )
                                        } setLoading={(id, key, value) =>
                                            setNotifications(prev =>
                                                prev.map(n =>
                                                    n.id === id
                                                        ? {
                                                            ...n,
                                                            loading: {
                                                                ...n.loading,
                                                                [key]: value
                                                            }
                                                        }
                                                        : n
                                                )
                                            )
                                        }
                                    />
                                ))}
                                <div ref={bottomRef} style={{ height: 1 }} />
                                {loadingPage && (
                                    <div style={{ textAlign: 'center', padding: 12 }}>
                                        {text[lang]['loading']}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </React.Fragment>
    );
};

export default Notifications;
